import React, { useState, useRef, useEffect } from 'react';
import { Download, ZoomIn, ZoomOut, FileText, BarChart3, GitCompare, Ticket } from 'lucide-react';
import SurveyCalculationReport from '../../components/reports/survey/SurveyCalculationReport.tsx';
import jsPDF from "jspdf";
import html2pdf from "html2pdf.js";
import html2PDF from 'jspdf-html2canvas';

// PDF Viewer Component for other reports
const PDFViewer = ({ file, scale, onScaleChange }) => {
  const [currentPage, setCurrentPage] = useState(1);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 dark:from-gray-800 dark:to-gray-900 p-6 h-full flex flex-col">
      {/* Viewer Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Document Viewer</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            Page {currentPage} of {file.pages || 5}
          </span>
        </div>
      </div>

      {/* Zoom Controls */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => onScaleChange(Math.max(0.5, scale - 0.1))}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          disabled={scale <= 0.5}
        >
          <ZoomOut size={18} />
        </button>
        <span className="text-sm font-medium text-gray-700">
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={() => onScaleChange(Math.min(2, scale + 0.1))}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          disabled={scale >= 2}
        >
          <ZoomIn size={18} />
        </button>
      </div>

      {/* PDF Content */}
      <div className="flex-1 bg-gray-50 rounded-lg border border-gray-200 overflow-auto flex items-center justify-center p-4">
        <div 
          className="bg-white shadow-lg rounded-lg p-8"
          style={{ transform: `scale(${scale})`, transformOrigin: 'center' }}
        >
          {/* Mock PDF Content */}
          <div className="text-center">
            <div className="text-4xl mb-4">📄</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{file.title}</h2>
            <p className="text-gray-600 mb-4">{file.description}</p>
            <div className="grid grid-cols-2 gap-4 text-left">
              <div>
                <h4 className="font-semibold text-gray-700">Report Details</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>Generated: {new Date().toLocaleDateString()}</li>
                  <li>Pages: {file.pages || 5}</li>
                  <li>Format: PDF</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-700">Content</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>Survey Data Analysis</li>
                  <li>Quality Metrics</li>
                  <li>Technical Specifications</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <button
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage <= 1}
          className="px-4 py-2 bg-gray-100 rounded-lg text-gray-700 disabled:opacity-50"
        >
          Previous
        </button>
        <button
          onClick={() => setCurrentPage(Math.min(file.pages || 5, currentPage + 1))}
          disabled={currentPage >= (file.pages || 5)}
          className="px-4 py-2 bg-gray-100 rounded-lg text-gray-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

// Report Card Component
const ReportCard = ({ report, isSelected, onSelect, onDownload }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'pre-survey':
        return <FileText size={20} className="text-blue-500 " />;
      case 'survey':
        return <BarChart3 size={20} className="text-green-500" />;
      case 'comparison':
        return <GitCompare size={20} className="text-purple-500" />;
      case 'job-ticket':
        return <Ticket size={20} className="text-orange-500" />;
      case 'quality-check':
        return <BarChart3 size={20} className="text-red-500" />;
      default:
        return <FileText size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div
      className={`p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-md ${
        isSelected
          ? 'border-red-500 bg-red-50'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
      onClick={() => onSelect(report)}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          {getIcon(report.type)}
          <div>
            <h3 className="font-semibold text-gray-800">{report.title}</h3>
            <p className="text-sm text-gray-500">{report.description}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
          {report.status}
        </span>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-500">
          {new Date(report.date).toLocaleDateString()} • {report.pages} pages
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDownload(report);
          }}
          className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          title="Download report"
        >
          <Download size={16} />
        </button>
      </div>
    </div>
  );
};

export default function ReportsTab({ jobInfo, wellInfo, currentRun }) {
  const [selectedReport, setSelectedReport] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const surveyReportRef = useRef();
  const viewerContainerRef = useRef();
  const [reports] = useState([
    {
      id: 1,
      type: 'pre-survey',
      title: 'Pre-Survey Report',
      description: 'Initial survey planning and analysis',
      status: 'completed',
      date: new Date().toISOString(),
      pages: 12,
      file: '/reports/pre-survey.pdf'
    },
    {
      id: 2,
      type: 'survey',
      title: 'Survey Calculation Report',
      description: 'Detailed survey measurements and calculations',
      status: 'completed',
      date: new Date().toISOString(),
      pages: 18,
      file: '/reports/survey-calculation.pdf'
    },
    {
      id: 3,
      type: 'comparison',
      title: 'Comparison Analysis',
      description: 'Comparison with previous survey data',
      status: 'processing',
      date: new Date().toISOString(),
      pages: 8,
      file: '/reports/comparison.pdf'
    },
    {
      id: 4,
      type: 'job-ticket',
      title: 'Job Ticket',
      description: 'Complete job documentation and tickets',
      status: 'completed',
      date: new Date().toISOString(),
      pages: 6,
      file: '/reports/job-ticket.pdf'
    },
    {
      id: 5,
      type: 'quality-check',
      title: 'Quality Check Report',
      description: 'Quality assessment and validation',
      status: 'completed',
      date: new Date().toISOString(),
      pages: 10,
      file: '/reports/quality-check.pdf',

    },
  ]);
  const jobId = jobInfo?.id;
  const wellId = wellInfo?.id;
  const runId = currentRun?.id;

useEffect(() => {
  const updateZoomLevel = () => {
    if (!viewerContainerRef.current || !surveyReportRef.current) return;

    const containerWidth = viewerContainerRef.current.offsetWidth;
    const reportWidth = surveyReportRef.current.offsetWidth;

    const newZoom = Math.min(1, containerWidth / reportWidth);

    setZoomLevel(newZoom);
  };

  const resizeObserver = new ResizeObserver(updateZoomLevel);
  if (viewerContainerRef.current) resizeObserver.observe(viewerContainerRef.current);

  updateZoomLevel(); 

  return () => resizeObserver.disconnect();
}, []);

const handleDownload = (report) => {
  if (report.type === "survey") {
    if (surveyReportRef.current) {
      const element = surveyReportRef.current.cloneNode(true);
      console.log("Cloned element for PDF:", element);

      const emptyElements = element.querySelectorAll('.empty, .spacer, [style*="height: 0"]');
      emptyElements.forEach(el => el.remove());

      element.setAttribute('style', 'vertical-align: top; overflow: hidden;');
      
      // Wrap in a div with PDF-friendly styles
      const wrapper = document.createElement("div");
        wrapper.style.width = "210mm";
        wrapper.style.minHeight = "297mm";
        wrapper.style.padding = "0";
        wrapper.style.margin = "0";
        wrapper.style.overflow = "hidden";
        wrapper.style.boxSizing = "border-box";
        wrapper.style.fontFamily = "calibri, sans-serif";
        wrapper.appendChild(element);
      

     
      const opt = {
        margin: [0, 0, 0, 0],  
        filename: `${report.title.toLowerCase().replace(/\s+/g, "-")}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { 
          scale: 2,  
          useCORS: true,
            
          letterRendering: true,  
          bottom: undefined  
        },
        pagebreak: { 
          mode: ['css', 'legacy'], 
          avoid: '.no-break',  
          before: '.new-page'  
        },
        jsPDF: { unit: "mm", format: 'a4', orientation: "portrait" },
      };

      
      setTimeout(() => {
        html2pdf().set(opt).from(wrapper).toPdf().get('pdf').then(pdf => {
  const lastPage = pdf.internal.getNumberOfPages();
  const pageText = pdf.getPageInfo(lastPage)?.text || "";
  if (!pageText.trim()) {
    pdf.deletePage(lastPage);
  }
  pdf.save();
});
      }, 100);
}
  } else {

    const link = document.createElement("a");
    link.href = report.file;
    link.download = `${report.title.toLowerCase().replace(/\s+/g, "-")}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  console.log("Downloading report:", report);
};
// const handleDownload = (report) => {
//   if (report.type === "survey") {
//     if (surveyReportRef.current) {
//       // Clone the element
//       const element = surveyReportRef.current.cloneNode(true);
//       console.log("Cloned element for PDF:", element);

//       // Apply styles to the cloned element
//       element.setAttribute('style', 'vertical-align: top; overflow: hidden;');

//       // Wrap in a div with PDF-friendly styles
//       const wrapper = document.createElement("div");
//       wrapper.appendChild(element);

//       // Open a new tab to display the content
//       const newTab = window.open("", "_blank");
//       if (newTab) {
//         // Write the content to the new tab
//         newTab.document.write(`
//           <!DOCTYPE html>
//           <html>
//           <head>
//             <title>${report.title}</title>
//             <style>
//               body { font-family: Calibri, sans-serif; margin: 0; padding: 0; }
//               .wrapper { width: 210mm; min-height: 297mm; }
//             </style>
//           </head>
//           <body>
//             ${wrapper.outerHTML}
//           </body>
//           </html>
//         `);
//         newTab.document.close();

//         // PDF generation options
//         const opt = {
//           margin: [0, 0, 0, 0],
//           filename: `${report.title.toLowerCase().replace(/\s+/g, "-")}.pdf`,
//           image: { type: "jpeg", quality: 0.98 },
//           html2canvas: {
//             scale: 2,
//             useCORS: true,
//             letterRendering: true,
//           },
//           jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
//         };

//         // Generate and save the PDF
//         setTimeout(() => {
//           html2pdf().set(opt).from(wrapper).save();
//         }, 100);
//       } else {
//         console.error("Failed to open new tab. Please allow popups.");
//       }
//     }
//   } else {
//     // Handle non-survey report downloads
//     const link = document.createElement("a");
//     link.href = report.file;
//     link.download = `${report.title.toLowerCase().replace(/\s+/g, "-")}.pdf`;
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   }
//   console.log("Downloading report:", report);
// };


  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(2, prev + 0.1));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(0.5, prev - 0.1));
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Reports List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Available Reports</h2>
            <div className="text-sm text-gray-500">
              {reports.filter(r => r.status === 'completed').length} ready
            </div>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
            {reports.map((report) => (
              <ReportCard
                key={report.id}
                report={report}
                isSelected={selectedReport?.id === report.id}
                onSelect={setSelectedReport}
                onDownload={handleDownload}
              />
            ))}
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-2xl p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-3">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  const allReports = reports.filter(r => r.status === 'completed');
                  console.log('Downloading all completed reports');
                }}
                className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Download size={16} />
                <span className="text-sm">Download All</span>
              </button>
              <button
                onClick={() => {
                  // Generate new report functionality
                  console.log('Generate new report');
                }}
                className="flex items-center gap-2 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
              >
                <FileText size={16} />
                <span className="text-sm">Generate New</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - PDF Viewer or Survey Report */}
        <div className="space-y-6" ref={viewerContainerRef}>
          {selectedReport ? (
            selectedReport.type === 'survey' ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 h-full flex flex-col dark:gray-800 dark:to-gray-900">
                {/* Viewer Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Document Viewer</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      Page 1 of 1
                    </span>
                  </div>
                </div>

                {/* Zoom Controls */}
                <div className="flex items-center gap-2 mb-4">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 0.5}
                    className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <ZoomOut size={18} />
                  </button>
                  <span className="text-sm font-medium text-gray-700">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 2}
                    className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <ZoomIn size={18} />
                  </button>
                </div>

                {/* Survey Report Content */}
                <div
  className="flex bg-gray-50 rounded-lg border border-gray-200 overflow-hidden p-4 flex justify-center"style={{ height: "750px", overflowY: "auto",         // vertical scroll
    overflowX: "auto",         // horizontal scroll
    whiteSpace: "normal",}}
>
  <div
    style={{
      transform: `scale(${zoomLevel})`,
      transformOrigin: "top center",
      display: "inline-block", // ensures container size respects scaled content
    }}
  >
    <div ref={surveyReportRef} className="w-full max-w-[1000px] mx-auto"style={{
      transform: `scale(${zoomLevel})`,
      transformOrigin: "top center",
      display: "inline-block", 
    }}>
      <SurveyCalculationReport
        jobInfo={jobInfo}
        wellInfo={wellInfo}
        currentRun={currentRun}
        jobId={jobId}
        wellId={wellId}
        runId={runId}
      />
    </div>
  </div>
</div>
              </div>
            ) : (
              <PDFViewer
                file={selectedReport}
                scale={zoomLevel}
                onScaleChange={setZoomLevel}
              />
            )
          ) : (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 h-full flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="text-6xl mb-4">📊</div>
                <h3 className="text-lg font-semibold mb-2">No Report Selected</h3>
                <p className="text-sm">Select a report from the list to view its contents</p>
              </div>
            </div>
          )}

          {/* Zoom Controls Footer */}
          {selectedReport && (
            <div className="bg-white rounded-2xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">Zoom:</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= 0.5}
                      className="p-1 rounded bg-gray-100 disabled:opacity-50"
                    >
                      <ZoomOut size={16} />
                    </button>
                    <span className="text-sm font-medium w-12 text-center">
                      {Math.round(zoomLevel * 100)}%
                    </span>
                    <button
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= 2}
                      className="p-1 rounded bg-gray-100 disabled:opacity-50"
                    >
                      <ZoomIn size={16} />
                    </button>
                  </div>
                </div>
                
                <button
                  onClick={() => handleDownload(selectedReport)}
                  
                  className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Download size={16} />
                  Download PDF
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Report Statistics */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Report Statistics</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">
              {reports.filter(r => r.status === 'completed').length}
            </div>
            <div className="text-sm text-blue-700">Completed</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">
              {reports.filter(r => r.status === 'processing').length}
            </div>
            <div className="text-sm text-yellow-700">Processing</div>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-600">
              {reports.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-700">Pending</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {reports.reduce((total, report) => total + report.pages, 0)}
            </div>
            <div className="text-sm text-green-700">Total Pages</div>
          </div>
        </div>
      </div>
    </div>
  );
}